<script setup lang="ts">
import VideoCallAudio from "~/components/video-call/VideoCallAudio.vue";
import VideoCallChatPanel from "~/components/video-call/VideoCallChatPanel.vue";
import VideoCallConnectionDiagnostics from "~/components/video-call/VideoCallConnectionDiagnostics.vue";
import VideoCallHostToolsDialog from "~/components/video-call/VideoCallHostToolsDialog.vue";
import VideoCallMediaSettings from "~/components/video-call/VideoCallMediaSettings.vue";
import VideoCallParticipantRemovalDialog from "~/components/video-call/VideoCallParticipantRemovalDialog.vue";
import VideoCallPresentationStage from "~/components/video-call/VideoCallPresentationStage.vue";
import VideoCallRoomsControls from "~/components/video-call/VideoCallRoomsControls.vue";
import VideoCallRoomsStatus from "~/components/video-call/VideoCallRoomsStatus.vue";
import VideoCallVideo from "~/components/video-call/VideoCallVideo.vue";
import { useAuthStore } from "~/stores/auth";
import { useVideoCallsStore } from "~/stores/videoCalls";
import type {
  VideoCallPanelSize,
  VideoCallParticipant,
} from "~/types/videoCalls";
import { isIndividualParticipantVolumeSupported } from "~/utils/videoCalls/participantVolume.client";
import {
  getVideoCallAttention,
  getVideoCallMediaVisibleParticipantIds,
} from "~/utils/videoCalls/policy";

const props = withDefaults(
  defineProps<{
    embedded?: boolean;
    /** The host owns positioning; the panel only offers the handle. */
    draggable?: boolean;
    dragging?: boolean;
  }>(),
  {
    embedded: false,
    draggable: false,
    dragging: false,
  },
);

const emit = defineEmits<{
  "drag-start": [event: PointerEvent];
  "drag-keydown": [event: KeyboardEvent];
}>();

/** How long a tap keeps the focus-mode controls on screen. */
const FOCUS_CONTROLS_HOLD_MS = 3_500;

const store = useVideoCallsStore();
const authStore = useAuthStore();
const copied = ref(false);
const elapsedSeconds = ref(0);
const participantVolumeSupported = ref(false);
const participantVolumeSupport = ref<Record<string, boolean>>({});
const participantPendingRemoval = ref<VideoCallParticipant | null>(null);
const removalTriggerElement = ref<HTMLElement | null>(null);
const removalAnnouncement = ref("");
const minimizeButton = ref<HTMLButtonElement | null>(null);
const chatButton = ref<HTMLButtonElement | null>(null);
const hostToolsButton = ref<HTMLButtonElement | null>(null);
const hostToolsOpen = ref(false);
const focusControlsPinned = ref(false);
const presentationFullscreen = ref(false);
let elapsedTimer: ReturnType<typeof setInterval> | null = null;
let focusControlsTimer: ReturnType<typeof setTimeout> | null = null;

const localParticipant = computed<VideoCallParticipant>(() => {
  const fromConnection = store.connection?.participant;
  if (fromConnection) {
    return {
      ...fromConnection,
      camera_enabled: store.cameraEnabled,
      microphone_enabled: store.microphoneEnabled,
    };
  }

  return {
    id: "local",
    name:
      store.role === "host"
        ? `${authStore.user.firstName} ${authStore.user.lastName}`.trim() ||
          "Professor"
        : store.joinRequest?.name || "Você",
    role: store.role ?? "guest",
    state: "connected",
    is_connected: true,
    camera_enabled: store.cameraEnabled,
    microphone_enabled: store.microphoneEnabled,
  };
});

const remoteParticipants = computed(() =>
  store.participants.filter(
    (participant) =>
      participant.id !== localParticipant.value.id &&
      !["left", "removed"].includes(participant.state),
  ),
);

/**
 * The embedded page owns its own fixed layout, so it opts out of the size axis
 * entirely: no size class, never compact, and it reports as large only for the
 * remote-quality scale.
 */
const sizeClass = computed(() =>
  props.embedded ? null : `video-call-panel--${store.panelSize}`,
);
const presentationScale = computed<VideoCallPanelSize>(() =>
  presentationFullscreen.value || props.embedded ? "large" : store.panelSize,
);
const compact = computed(() => !props.embedded && store.panelSize === "small");
const draggable = computed(() => props.draggable && store.panelDraggable);

/**
 * Focus mode shows one face: whoever is speaking. Silence falls back to the
 * teacher, since in a class that is who the room is usually watching.
 */
const focusedParticipant = computed(() => {
  const speaking = remoteParticipants.value.find(
    (participant) => participant.id === store.activeSpeakerId,
  );
  if (speaking) return speaking;

  return (
    remoteParticipants.value.find(
      (participant) => participant.role === "host",
    ) ??
    remoteParticipants.value[0] ??
    null
  );
});

/** Audio always covers everyone; only the rendered tiles are narrowed down. */
const visibleRemoteParticipants = computed(() => {
  if (!compact.value) return remoteParticipants.value;
  return focusedParticipant.value ? [focusedParticipant.value] : [];
});

// Alone in the room there is no face to focus, so the self view stands in.
const showLocalTile = computed(
  () => !compact.value || !focusedParticipant.value,
);
const visibleCameraTileCount = computed(
  () => visibleRemoteParticipants.value.length + (showLocalTile.value ? 1 : 0),
);

const canViewConnectionDiagnostics = computed(
  () =>
    authStore.profileLoaded &&
    authStore.user.permissions.isTeacher &&
    store.role === "host" &&
    remoteParticipants.value.length > 0,
);

/**
 * Rendering is driven by the arbitrated presenter, never by the presence of a
 * screen track: an idle screen slot exists on every peer connection.
 */
const presentation = computed(() => {
  if (store.screenSharePending) {
    const stream = store.getLocalScreenStream();
    if (!stream) return null;

    return {
      stream,
      name: localParticipant.value.name,
      local: true,
      pending: true,
    };
  }

  const presenterId = store.presenterParticipantId;
  if (!presenterId) return null;

  if (presenterId === localParticipant.value.id) {
    const stream = store.getLocalScreenStream();
    if (!stream) return null;

    return {
      stream,
      name: localParticipant.value.name,
      local: true,
      pending: false,
    };
  }

  const participant = remoteParticipants.value.find(
    (candidate) => candidate.id === presenterId,
  );
  if (!participant) return null;

  return {
    stream: store.getRemoteScreenStream(presenterId),
    name: participant.name,
    local: false,
    pending: false,
  };
});

const remotePresenterParticipantId = computed(() => {
  const presenterId = store.presenterParticipantId;
  if (!presenterId || presenterId === localParticipant.value.id) return null;
  return remoteParticipants.value.some(
    (participant) => participant.id === presenterId,
  )
    ? presenterId
    : null;
});

const mediaVisibleParticipantIds = computed<string[] | null>(() => {
  return getVideoCallMediaVisibleParticipantIds({
    compact: compact.value,
    fullscreen: presentationFullscreen.value,
    visibleParticipantIds:
      compact.value && presentation.value
        ? []
        : visibleRemoteParticipants.value.map((participant) => participant.id),
    remotePresenterParticipantId: remotePresenterParticipantId.value,
  });
});

const setPresentationFullscreen = (fullscreen: boolean) => {
  presentationFullscreen.value = fullscreen;
};

const someoneElseIsPresenting = computed(
  () => Boolean(store.presenterParticipantId) && !store.isPresenting,
);
const sharingOrPending = computed(
  () => store.isPresenting || store.screenSharePending,
);

const screenShareLabel = computed(() => {
  if (store.screenSharePending) return "Cancelar compartilhamento";
  if (store.isPresenting) return "Parar de compartilhar a tela";
  if (someoneElseIsPresenting.value) {
    return `${store.presenter?.name ?? "Outro participante"} está compartilhando a tela`;
  }
  return "Compartilhar tela";
});

const formattedDuration = computed(() => {
  const minutes = Math.floor(elapsedSeconds.value / 60);
  const seconds = elapsedSeconds.value % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
});
const callAttention = computed(() =>
  getVideoCallAttention({
    connectionState: store.connectionState,
    errorMessage: store.errorMessage,
    pendingRequestCount: store.pendingJoinRequests.length,
  }),
);
const chatUnreadLabel = computed(() =>
  store.chatUnreadCount === 1
    ? "1 mensagem não lida"
    : `${store.chatUnreadCount} mensagens não lidas`,
);
const hostToolsLabel = computed(() =>
  store.pendingJoinRequests.length
    ? `Abrir convite e sala de espera, ${store.pendingJoinRequests.length} ${
        store.pendingJoinRequests.length === 1
          ? "pedido pendente"
          : "pedidos pendentes"
      }`
    : "Abrir convite e sala de espera",
);

const updateElapsed = () => {
  const startedAt = store.call?.started_at
    ? new Date(store.call.started_at).getTime()
    : Date.now();
  elapsedSeconds.value = Math.max(
    0,
    Math.floor((Date.now() - startedAt) / 1_000),
  );
};

const copyInvitation = async () => {
  if (!store.shareUrl) return;

  try {
    await navigator.clipboard.writeText(store.shareUrl);
  } catch {
    const input = document.createElement("textarea");
    input.value = store.shareUrl;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    input.remove();
  }

  copied.value = true;
  window.setTimeout(() => {
    copied.value = false;
  }, 2_000);
};

const openHostTools = (): void => {
  hostToolsOpen.value = true;
};

const closeHostTools = async (): Promise<void> => {
  if (store.loading) return;

  hostToolsOpen.value = false;
  await nextTick();
  hostToolsButton.value?.focus();
};

/**
 * Focus mode has no header, so the video frame itself is the drag surface.
 * At the larger sizes only the header drags, which is the usual convention.
 * Controls always keep working; the explicit handle is the one exception,
 * because it exists to be grabbed.
 */
const onDragPointerDown = (event: PointerEvent) => {
  if (!draggable.value) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  if (
    target.closest("button, a, input, select, [role='button']") &&
    !target.closest("[data-drag-handle]")
  ) {
    return;
  }
  if (!compact.value && !target.closest(".video-call-panel__header")) return;

  emit("drag-start", event);
};

/**
 * Touch has no hover, so a tap on the frame reveals the controls for a few
 * seconds. Mouse and keyboard are covered by :hover and :focus-within.
 */
const revealFocusControls = () => {
  if (!compact.value || props.dragging) return;

  focusControlsPinned.value = true;
  if (focusControlsTimer) clearTimeout(focusControlsTimer);
  focusControlsTimer = setTimeout(() => {
    focusControlsPinned.value = false;
    focusControlsTimer = null;
  }, FOCUS_CONTROLS_HOLD_MS);
};

const leave = () => {
  if (store.role === "host") {
    store.endHostCall();
  } else {
    store.leaveGuestCall();
  }
};

// Chat needs room the compact size does not have, so it grows the panel first.
const openChatFromCompact = async () => {
  if (store.panelSize === "small") store.setPanelSize("medium");
  await nextTick();
  store.setChatOpen(true);
};

const toggleChat = async () => {
  if (store.chatOpen) {
    store.setChatOpen(false);
    return;
  }
  await openChatFromCompact();
};

const closeChat = async () => {
  store.setChatOpen(false);
  await nextTick();
  chatButton.value?.focus();
};

const syncChatVisibility = () => {
  const documentVisible =
    typeof document === "undefined" || document.visibilityState === "visible";
  store.setChatVisible(store.chatOpen && documentVisible);
};

const isParticipantVolumeSupported = (participantId: string) =>
  participantVolumeSupported.value &&
  participantVolumeSupport.value[participantId] !== false;

const updateParticipantVolumeSupport = (
  participantId: string,
  supported: boolean,
) => {
  participantVolumeSupport.value = {
    ...participantVolumeSupport.value,
    [participantId]: supported,
  };
};

const focusAfterRemoval = async (
  preferredTarget: HTMLElement | null,
): Promise<void> => {
  await nextTick();
  (minimizeButton.value ?? preferredTarget)?.focus();
};

const requestParticipantRemoval = (participant: VideoCallParticipant): void => {
  if (store.removingParticipantId) return;

  store.clearParticipantRemovalError();
  removalTriggerElement.value =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  participantPendingRemoval.value = participant;
};

const cancelParticipantRemoval = async (): Promise<void> => {
  if (store.removingParticipantId) return;

  const trigger = removalTriggerElement.value;
  participantPendingRemoval.value = null;
  removalTriggerElement.value = null;
  store.clearParticipantRemovalError();
  await nextTick();
  trigger?.focus();
};

const confirmParticipantRemoval = async (): Promise<void> => {
  const participant = participantPendingRemoval.value;
  if (!participant || store.removingParticipantId) return;

  const removed = await store.removeParticipant(participant.id);
  if (!removed) return;

  participantPendingRemoval.value = null;
  removalTriggerElement.value = null;
  removalAnnouncement.value = `${participant.name} foi removido da chamada.`;
  await focusAfterRemoval(null);
};

watch(
  () => remoteParticipants.value.map((participant) => participant.id),
  async (participantIds) => {
    const pendingParticipant = participantPendingRemoval.value;
    if (!pendingParticipant || participantIds.includes(pendingParticipant.id)) {
      return;
    }

    const trigger = removalTriggerElement.value;
    participantPendingRemoval.value = null;
    removalTriggerElement.value = null;
    store.clearParticipantRemovalError();
    await focusAfterRemoval(trigger);
  },
);

// Every size renders video now, so presentation stays active while the panel
// is mounted; the rendered size only caps how much remote video is requested.
watch(
  presentationScale,
  (scale) => {
    store.setVideoPresentationActive(true);
    store.setVideoPresentationScale(scale);
  },
  { immediate: true },
);

// Peers without a tile are asked to pause, so focus mode costs one stream
// instead of one per participant.
watch(
  () => {
    const participantIds = mediaVisibleParticipantIds.value;
    return participantIds === null ? null : participantIds.join(",");
  },
  (visibleIds) => {
    store.setVisibleParticipants(
      visibleIds === null ? null : visibleIds ? visibleIds.split(",") : [],
    );
  },
  { immediate: true },
);

watch(presentation, (activePresentation) => {
  if (!activePresentation || activePresentation.local) {
    presentationFullscreen.value = false;
  }
});

watch([() => store.chatOpen], syncChatVisibility, { immediate: true });

onMounted(() => {
  participantVolumeSupported.value = isIndividualParticipantVolumeSupported();
  document.addEventListener("visibilitychange", syncChatVisibility);
  updateElapsed();
  elapsedTimer = setInterval(updateElapsed, 1_000);
});

onBeforeUnmount(() => {
  if (elapsedTimer) clearInterval(elapsedTimer);
  if (focusControlsTimer) clearTimeout(focusControlsTimer);
  document.removeEventListener("visibilitychange", syncChatVisibility);
  store.setChatVisible(false);
  store.setVideoPresentationActive(false);
  store.setVisibleParticipants(null);
});
</script>

<template>
  <section
    class="video-call-panel"
    :class="[
      sizeClass,
      {
        'video-call-panel--embedded': props.embedded,
        'video-call-panel--chat-open': store.chatOpen,
        'video-call-panel--dragging': props.dragging,
        'video-call-panel--controls-visible': focusControlsPinned,
      },
    ]"
    data-sentry-block
    data-testid="video-call-panel"
    @pointerdown="onDragPointerDown"
    @click="revealFocusControls"
  >
    <VideoCallAudio
      v-for="participant in remoteParticipants"
      :key="`audio-${participant.id}`"
      :stream="store.getRemoteStream(participant.id)"
      :participant-id="participant.id"
      :volume-percent="store.participantVolumePercent[participant.id] ?? 100"
      @volume-support-change="
        updateParticipantVolumeSupport(participant.id, $event)
      "
    />

    <header
      class="video-call-panel__header"
      :class="{ 'video-call-panel__header--draggable': draggable }"
    >
      <div class="video-call-panel__identity">
        <span class="video-call-panel__eyebrow"
          ><i class="mdi mdi-circle" /> Chamada ao vivo</span
        >
        <h2>
          {{ formattedDuration }} · {{ store.participantCount }}
          {{ store.participantCount === 1 ? "participante" : "participantes" }}
        </h2>
        <span
          v-if="callAttention"
          class="video-call-panel__summary-attention"
          :class="`video-call-panel__summary-attention--${callAttention.kind}`"
          :role="callAttention.kind === 'error' ? 'alert' : 'status'"
        >
          {{ callAttention.message }}
        </span>
      </div>
      <div class="video-call-panel__header-actions">
        <VideoCallConnectionDiagnostics
          v-if="canViewConnectionDiagnostics && !compact"
          :participants="remoteParticipants"
          :diagnostics="store.peerDiagnostics"
          :peer-states="store.peerStates"
          @opened="store.refreshPeerDiagnostics"
        />
        <button
          v-if="draggable"
          class="icon-button video-call-panel__drag-handle"
          type="button"
          aria-label="Mover a chamada. Use as setas para reposicionar"
          title="Mover a chamada"
          data-testid="video-call-drag-handle"
          data-drag-handle
          @keydown="emit('drag-keydown', $event)"
        >
          <i class="mdi mdi-cursor-move" aria-hidden="true" />
        </button>
        <button
          v-if="!props.embedded && store.canShrinkPanel"
          class="icon-button"
          type="button"
          aria-label="Diminuir a chamada"
          title="Diminuir a chamada"
          data-testid="video-call-shrink-control"
          @click="store.shrinkPanel"
        >
          <i class="mdi mdi-arrow-collapse" aria-hidden="true" />
        </button>
        <button
          v-if="!props.embedded && store.canGrowPanel"
          ref="minimizeButton"
          class="icon-button"
          type="button"
          aria-label="Aumentar a chamada"
          title="Aumentar a chamada"
          data-testid="video-call-grow-control"
          @click="store.growPanel"
        >
          <i class="mdi mdi-arrow-expand" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="video-call-panel__stage">
      <div
        class="video-call-panel__main"
        :class="{
          'video-call-panel__main--rooms': store.roomsSnapshot && !compact,
        }"
      >
        <VideoCallRoomsStatus :compact="compact" />
        <div
          v-if="store.connectionState === 'reconnecting'"
          class="video-call-panel__connection"
          role="status"
        >
          <i class="mdi mdi-loading mdi-spin" />
          Reconectando a chamada...
        </div>

        <div
          class="video-call-panel__media"
          :class="{
            'video-call-panel__media--presenting': presentation,
            'video-call-panel__media--presentation-only':
              compact && presentation,
          }"
        >
          <VideoCallPresentationStage
            v-if="presentation"
            :stream="presentation.stream"
            :presenter-name="presentation.name"
            :local="presentation.local"
            :pending="presentation.pending"
            @fullscreen-change="setPresentationFullscreen"
          />

          <div
            v-if="!compact || !presentation"
            class="video-call-panel__grid"
            :data-participant-count="visibleCameraTileCount"
          >
            <VideoCallVideo
              v-if="showLocalTile"
              :stream="store.getLocalStream()"
              :name="localParticipant.name"
              local
              fill
              :camera-enabled="store.cameraEnabled"
              :microphone-enabled="store.microphoneEnabled"
            />
            <VideoCallVideo
              v-for="participant in visibleRemoteParticipants"
              :key="participant.id"
              :stream="store.getRemoteStream(participant.id)"
              :name="participant.name"
              fill
              :camera-enabled="participant.camera_enabled !== false"
              :microphone-enabled="participant.microphone_enabled !== false"
              :connection-state="store.peerStates[participant.id]"
              :participant-state="participant.state"
              :account-linked="participant.account_linked === true"
              :quality-profile="
                store.remoteVideoQuality[participant.id]?.profile
              "
              :removable="
                !compact && store.role === 'host' && participant.role !== 'host'
              "
              :volume-percent="
                store.participantVolumePercent[participant.id] ?? 100
              "
              :volume-supported="
                !compact && isParticipantVolumeSupported(participant.id)
              "
              muted
              @remove="requestParticipantRemoval(participant)"
              @update:volume-percent="
                store.setParticipantVolume(participant.id, $event)
              "
            />
          </div>
        </div>

        <p
          v-if="store.screenShareError"
          class="video-call-panel__screen-share-error"
          role="alert"
        >
          <i class="mdi mdi-alert-circle-outline" aria-hidden="true" />
          <span>{{ store.screenShareError }}</span>
          <button
            type="button"
            class="video-call-panel__screen-share-dismiss"
            aria-label="Fechar aviso"
            @click="store.dismissScreenShareError"
          >
            <i class="mdi mdi-close" aria-hidden="true" />
          </button>
        </p>

        <p v-if="store.mediaError" class="video-call-panel__error" role="alert">
          {{ store.mediaError }}
        </p>

        <p
          v-if="store.errorMessage && !hostToolsOpen"
          class="video-call-panel__error"
          role="alert"
        >
          {{ store.errorMessage }}
        </p>
      </div>

      <VideoCallChatPanel
        v-if="store.chatEnabled && store.chatOpen"
        :messages="store.chatMessages"
        :current-participant-id="localParticipant.id"
        :max-body-length="store.chatMaxBodyLength"
        @close="closeChat"
        @send="store.sendChatMessage"
        @retry="store.retryChatMessage"
      />
    </div>

    <footer class="video-call-panel__controls">
      <button
        class="control-button"
        type="button"
        :aria-label="
          store.microphoneEnabled ? 'Desligar microfone' : 'Ligar microfone'
        "
        :title="
          store.microphoneEnabled ? 'Desligar microfone' : 'Ligar microfone'
        "
        @click="store.toggleMicrophone"
      >
        <i
          class="mdi"
          :class="
            store.microphoneEnabled ? 'mdi-microphone' : 'mdi-microphone-off'
          "
        />
      </button>
      <button
        class="control-button"
        type="button"
        :aria-label="store.cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'"
        :title="store.cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'"
        @click="store.toggleCamera"
      >
        <i
          class="mdi"
          :class="store.cameraEnabled ? 'mdi-video' : 'mdi-video-off-outline'"
        />
      </button>
      <button
        v-if="store.canShareScreen"
        class="control-button"
        :class="{ 'control-button--active': sharingOrPending }"
        type="button"
        :disabled="store.screenShareBusy || someoneElseIsPresenting"
        :aria-label="screenShareLabel"
        :aria-pressed="sharingOrPending"
        :title="screenShareLabel"
        data-testid="video-call-screen-share-control"
        @click="store.toggleScreenShare"
      >
        <i
          class="mdi"
          :class="sharingOrPending ? 'mdi-monitor-off' : 'mdi-monitor-share'"
          aria-hidden="true"
        />
      </button>
      <VideoCallMediaSettings v-if="!compact" />
      <button
        v-if="store.chatEnabled"
        ref="chatButton"
        class="control-button video-call-panel__chat-button"
        :class="{ 'control-button--active': store.chatOpen }"
        type="button"
        :aria-label="
          store.chatOpen
            ? 'Fechar chat da chamada'
            : store.chatUnreadCount
              ? `Abrir chat da chamada, ${chatUnreadLabel}`
              : 'Abrir chat da chamada'
        "
        :aria-pressed="store.chatOpen"
        @click="toggleChat"
      >
        <i class="mdi mdi-message-text-outline" aria-hidden="true" />
        <span>Chat</span>
        <span
          v-if="store.chatUnreadCount"
          class="video-call-panel__chat-badge"
          :aria-label="`${store.chatUnreadCount} mensagens não lidas`"
        >
          {{ store.chatUnreadCount > 99 ? "99+" : store.chatUnreadCount }}
        </span>
      </button>
      <button
        v-if="!compact && store.role === 'host'"
        ref="hostToolsButton"
        class="control-button control-button--invite"
        type="button"
        :disabled="store.loading"
        :aria-label="hostToolsLabel"
        :title="hostToolsLabel"
        :aria-expanded="hostToolsOpen"
        aria-haspopup="dialog"
        data-testid="video-call-invitation-control"
        @click="openHostTools"
      >
        <i class="mdi mdi-account-multiple-plus-outline" aria-hidden="true" />
        <span
          v-if="store.pendingJoinRequests.length"
          class="video-call-panel__host-tools-badge"
          aria-hidden="true"
        >
          {{ store.pendingJoinRequests.length }}
        </span>
      </button>
      <VideoCallRoomsControls
        :compact="compact"
        @waiting-room="openHostTools"
      />
      <button
        class="control-button control-button--danger"
        type="button"
        :disabled="store.loading"
        :aria-label="
          store.role === 'host' ? 'Encerrar chamada' : 'Sair da chamada'
        "
        @click="leave"
      >
        <i class="mdi mdi-phone-hangup" />
        <span>{{ store.role === "host" ? "Encerrar" : "Sair" }}</span>
      </button>
    </footer>

    <VideoCallParticipantRemovalDialog
      :model-value="Boolean(participantPendingRemoval)"
      :participant-name="participantPendingRemoval?.name ?? ''"
      :loading="store.removingParticipantId === participantPendingRemoval?.id"
      :error-message="store.participantRemovalError"
      @cancel="cancelParticipantRemoval"
      @confirm="confirmParticipantRemoval"
    />

    <VideoCallHostToolsDialog
      :model-value="hostToolsOpen"
      :share-url="store.shareUrl"
      :max-participants="store.limits.max_participants"
      :pending-requests="store.pendingJoinRequests"
      :at-capacity="store.atCapacity"
      :loading="store.loading"
      :copied="copied"
      :error-message="store.errorMessage"
      @close="closeHostTools"
      @copy="copyInvitation"
      @rotate="store.rotateInvitation"
      @approve="store.approveJoinRequest"
      @deny="store.denyJoinRequest"
    />

    <p class="video-call-panel__sr-only" aria-live="polite">
      {{ removalAnnouncement }}
    </p>
    <p class="video-call-panel__sr-only" aria-live="polite">
      {{ copied ? "Link de convite copiado." : "" }}
    </p>
  </section>
</template>

<style scoped lang="scss">
.video-call-panel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: min(720px, calc(100vw - 32px));
  height: min(560px, calc(100dvh - 32px));
  min-height: 0;
  overflow: hidden;
  border: 1px solid #dbe1ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 60px rgba(17, 30, 55, 0.24);
  color: #26334d;
}

/*
 * Focus mode is literally the speaker's frame: no title, no duration, no
 * participant count, no panel chrome. Every control becomes an overlay that
 * only appears on hover, keyboard focus, or a tap.
 */
.video-call-panel--small {
  position: relative;
  display: block;
  width: min(300px, calc(100vw - 24px));
  height: auto;
  max-height: calc(100dvh - 24px);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 0;
  border-radius: 14px;
  background: transparent;
  box-shadow: 0 18px 44px rgba(17, 30, 55, 0.32);

  /*
   * Only the alert survives here. The title, duration and participant count
   * are exactly the chrome focus mode is meant to drop.
   */
  .video-call-panel__identity {
    position: absolute;
    z-index: 3;
    top: 8px;
    left: 8px;
    /* Reserve the top-right corner so the alert never runs under the
       overlay buttons once they appear. */
    max-width: calc(100% - 132px);

    .video-call-panel__eyebrow,
    h2 {
      display: none;
    }
  }

  .video-call-panel__header {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    left: 0;
    justify-content: flex-end;
    padding: 8px;
    gap: 8px;
    /* The frame underneath stays grabbable between the overlay buttons. */
    pointer-events: none;
  }

  /* The reconnecting banner would push the frame; the badge already says it. */
  .video-call-panel__connection {
    display: none;
  }

  .video-call-panel__stage,
  .video-call-panel__main,
  .video-call-panel__media {
    width: 100%;
    height: 100%;
  }

  .video-call-panel__media {
    padding: 0;
  }

  .video-call-panel__grid {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 0;
  }

  .video-call-panel__controls {
    position: absolute;
    z-index: 2;
    right: 0;
    bottom: 0;
    left: 0;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 4px;
    padding: 14px 8px 8px;
    /* As a floating overlay it must not carry the panel's divider line. */
    border-top: 0;
    background: linear-gradient(rgba(13, 21, 38, 0), rgba(13, 21, 38, 0.82));

    > .control-button {
      flex: 0 0 32px;
      width: 32px;
      padding: 0;
      border-color: rgba(255, 255, 255, 0.22);
      background: rgba(19, 28, 48, 0.78);
      color: #fff;
    }

    /* Icon-only controls; labels would never fit at this width. */
    > .control-button > span:not(.video-call-panel__chat-badge) {
      display: none;
    }
  }

  /*
   * Alerts are not chrome: a pending join request must never hide behind
   * hover. The identity scope is needed to outrank the base rule that keeps
   * the notice on a single line at the larger sizes.
   */
  .video-call-panel__identity .video-call-panel__summary-attention {
    padding: 4px 8px;
    border-radius: 10px;
    background: rgba(13, 21, 38, 0.82);
    color: #fff;
    font-size: 0.68rem;
    line-height: 1.3;
    /* Truncating "2 pedidos aguardam aprovação" would destroy its meaning. */
    white-space: normal;
  }

  .video-call-panel__screen-share-error {
    position: absolute;
    z-index: 3;
    top: 36px;
    right: 8px;
    left: 8px;
    margin: 0;
    font-size: 0.72rem;
  }

  .video-call-panel__header-actions,
  .video-call-panel__controls {
    opacity: 0;
    transition: opacity 140ms ease;
    pointer-events: none;
  }
}

.video-call-panel--small:hover,
.video-call-panel--small:focus-within,
.video-call-panel--small.video-call-panel--controls-visible {
  .video-call-panel__header-actions,
  .video-call-panel__controls {
    opacity: 1;
    pointer-events: auto;
  }
}

.video-call-panel--small .video-call-panel__header-actions .icon-button {
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(19, 28, 48, 0.78);
  color: #fff;
}

.video-call-panel--medium {
  width: min(720px, calc(100vw - 32px));
  height: min(560px, calc(100dvh - 32px));
}

/*
 * Roughly 80% of the viewport. Deliberately not fullscreen, so the page
 * underneath stays visible and reachable.
 */
.video-call-panel--large {
  width: min(80vw, 1600px);
  height: min(80dvh, calc(100dvh - 32px));
}

/* Chat sets its own width; large keeps its share of the viewport. */
.video-call-panel--large.video-call-panel--chat-open {
  width: min(80vw, 1600px);
  height: min(80dvh, calc(100dvh - 32px));
}

.video-call-panel--dragging {
  user-select: none;
  box-shadow: 0 30px 72px rgba(17, 30, 55, 0.34);
}

.video-call-panel--embedded {
  width: min(920px, 100%);
  height: min(640px, calc(100dvh - 112px));
  max-height: calc(100dvh - 112px);
  margin: 0 auto;
}

/*
 * The chat brings its own footprint, except in focus mode, which never shows
 * it. Without the guard this rule would win on source order and blow the
 * compact panel up to full chat size.
 */
.video-call-panel--chat-open:not(.video-call-panel--small) {
  width: min(1060px, calc(100vw - 32px));
  height: min(680px, calc(100dvh - 32px));
}

.video-call-panel--embedded.video-call-panel--chat-open {
  width: min(1120px, 100%);
  height: min(640px, calc(100dvh - 112px));
}

.video-call-panel__stage {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.video-call-panel__main {
  position: relative;
  display: grid;
  flex: 1;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.video-call-panel__main--rooms {
  grid-template-rows: auto minmax(0, 1fr);
}

.video-call-panel__identity {
  display: grid;
  min-width: 0;
}

.video-call-panel__identity .video-call-panel__summary-attention {
  overflow: hidden;
  color: #9a5a00;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-call-panel__identity .video-call-panel__summary-attention--error {
  color: #b00020;
}

.video-call-panel__identity .video-call-panel__summary-attention--reconnecting {
  color: #214a9a;
}

.video-call-panel__header--draggable {
  cursor: move;
}

/* Touch drags must not scroll the page out from under the pointer. */
.video-call-panel__header--draggable,
.video-call-panel__drag-handle {
  touch-action: none;
}

.video-call-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px 12px;

  h2 {
    margin: 3px 0 0;
    color: #17233c;
    font-size: 1rem;
  }
}

.video-call-panel__header-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 8px;
}

.video-call-panel__eyebrow {
  color: #65728a;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  i {
    color: #e53935;
    font-size: 0.58rem;
  }
}

.video-call-panel__connection {
  position: absolute;
  z-index: 4;
  top: 8px;
  right: 18px;
  left: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 9px 11px;
  border-radius: 10px;
  font-size: 0.76rem;
  line-height: 1.35;
}

.video-call-panel__connection {
  background: #edf3ff;
  color: #214a9a;
}

.video-call-panel__media {
  display: grid;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 0 12px 12px;
  overflow: hidden;
}

.video-call-panel__media--presenting {
  grid-template-rows: minmax(0, 3fr) minmax(72px, 1fr);
  gap: 8px;
}

.video-call-panel__media--presentation-only {
  grid-template-rows: minmax(0, 1fr);
  gap: 0;
  padding: 0;
}

.video-call-panel__grid {
  display: grid;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 8px;
  overflow: hidden;
}

.video-call-panel__grid[data-participant-count="1"] {
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
}

.video-call-panel__grid[data-participant-count="2"] {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: minmax(0, 1fr);
}

.video-call-panel__media--presenting .video-call-panel__grid {
  grid-template-rows: minmax(0, 1fr);
}

.video-call-panel__media--presenting
  .video-call-panel__grid[data-participant-count="3"] {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.video-call-panel__media--presenting
  .video-call-panel__grid[data-participant-count="4"] {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

/* Filmstrip tiles are intentionally informational: their full-size volume and
 * removal actions cannot fit four-up on a phone without being clipped. */
.video-call-panel__media--presenting :deep(.video-tile__actions) {
  display: none;
}

.video-call-panel__screen-share-error {
  position: absolute;
  z-index: 4;
  right: 12px;
  bottom: 8px;
  left: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #f2c9c4;
  border-radius: 10px;
  background: #fdf3f2;
  color: #8c2f24;
  font-size: 0.82rem;
  line-height: 1.35;

  span {
    flex: 1;
  }
}

.video-call-panel__screen-share-dismiss {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  line-height: 1;
}

.video-call-panel__error {
  position: absolute;
  z-index: 4;
  top: 8px;
  right: 16px;
  left: 16px;
  margin: 0;
  padding: 9px 11px;
  border-radius: 9px;
  background: #fff0f1;
  color: #a51f34;
  font-size: 0.78rem;
}

.video-call-panel__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  white-space: nowrap;
}

.video-call-panel__controls {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
  padding: 12px 16px 16px;
  overflow: hidden;
  border-top: 1px solid #e8ecf4;
}

.icon-button,
.control-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  color: #273550;
  cursor: pointer;
}

.icon-button {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0f3f9;
}

.icon-button--danger {
  background: #fff0f1;
  color: #b00020;
}

.control-button {
  min-width: 44px;
  height: 44px;
  gap: 7px;
  padding: 0 13px;
  border-radius: 13px;
  background: #eef1f7;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.5;
  }
}

.control-button--danger {
  background: #b00020;
  color: #fff;
}

.control-button--active {
  background: #dfe6ff;
  color: #2448d8;
}

.video-call-panel__chat-button {
  position: relative;
}

.video-call-panel__chat-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  background: #c6283e;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  line-height: 1;
}

.control-button--invite {
  position: relative;
  color: #3156ff;
}

.video-call-panel__host-tools-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid #fff;
  border-radius: 999px;
  background: #c6283e;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  line-height: 1;
}

@media (orientation: portrait) {
  .video-call-panel__media:not(.video-call-panel__media--presenting)
    .video-call-panel__grid[data-participant-count="2"] {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .video-call-panel:not(.video-call-panel--small) {
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0;
  }

  .video-call-panel--embedded:not(.video-call-panel--small),
  .video-call-panel--embedded.video-call-panel--chat-open {
    height: calc(100dvh - 104px);
    max-height: calc(100dvh - 104px);
  }

  .video-call-panel__media {
    padding-right: 8px;
    padding-bottom: 8px;
    padding-left: 8px;
  }

  .video-call-panel__controls {
    gap: 6px;
    padding-right: 8px;
    padding-left: 8px;

    > .control-button {
      flex: 0 0 44px;
      width: 44px;
      padding: 0;
    }

    > .control-button > span:not(.video-call-panel__chat-badge) {
      display: none;
    }
  }
}
</style>
